// "Delete account" menu action + confirmation dialog. Required by Apple App
// Store Guideline 5.1.1(v): accounts must be deletable from inside the app.
// Calls the delete-account edge function (which wipes the user + their data),
// then signs out. Self-contained so it drops cleanly into the account menu.

import { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { deleteAccount } from "@/api/account";
import { useAuth } from "@/hooks/useAuth";

export function DeleteAccountButton() {
  const { signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const onConfirm = async () => {
    setBusy(true);
    try {
      await deleteAccount();
      toast.success("Your account and data have been deleted.");
      setOpen(false);
      await signOut();
    } catch (e) {
      toast.error("Couldn't delete your account", {
        description: e instanceof Error ? e.message : "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:bg-destructive/10 transition-smooth text-left text-destructive"
        >
          <Trash2 className="w-4 h-4" /> Delete account
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete your account?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes your account and everything tied to it — your saved
            routes, ratings, photos, tips, and road nominations. This can&rsquo;t be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void onConfirm();
            }}
            disabled={busy}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete account"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
